import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, FolderKanban, Calendar, MapPin,
  Globe, Hash, FileText, Clock, CheckCircle2, X
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';
import OEMAccessDenied from './OEMAccessDenied';

const OEMProjectCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    country: '',
    status: 'draft',
  });

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await api.get('/oem/dashboard/team/');
        const roles = response.data.my_roles || [];
        const hasMasterRole = roles.some(r => r.role === 'master');
        const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;
        if (hasOnlyRestrictedRoles) {
          setAccessDenied(true);
        }
      } catch (err) {
        // If endpoint fails, user has no team roles - allow access
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      // Prepare data - convert empty strings to null for optional fields
      const dataToSend = {
        ...formData,
        end_date: formData.end_date || null,
        description: formData.description || '',
        location: formData.location || '',
        country: formData.country || '',
      };
      const response = await api.post('/oem/projects/', dataToSend);
      toast.success('Project created successfully!');
      navigate(`/oem/projects/${response.data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      // Handle validation errors
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          // Get first error message
          const firstError = Object.entries(errorData)[0];
          if (firstError) {
            const [field, messages] = firstError;
            const message = Array.isArray(messages) ? messages[0] : messages;
            toast.error(`${field}: ${message}`);
          } else {
            toast.error('Failed to create project');
          }
        } else {
          toast.error(errorData.detail || errorData || 'Failed to create project');
        }
      } else {
        toast.error('Failed to create project');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingPermissions) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <OEMAccessDenied message="You don't have permission to create Projects." />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/oem/projects')}
            className="w-10 h-10 bg-white rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:border-orange-300 transition-all shadow-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-black text-slate-900 uppercase tracking-tight">New Project</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">OEM Project Setup</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[100px] rounded-full"></div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FolderKanban className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-black text-white uppercase tracking-tight">Project Details</h2>
                <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mt-0.5">Configure your new OEM project</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Basic Information */}
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Basic Information</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Project identity</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    Project Name <span className="text-orange-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Wind Farm Bokel Installation"
                    className="w-full h-12 px-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    Project Code <span className="text-orange-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      placeholder="e.g., TR-BOKEL-2025"
                      className="w-full h-12 pl-11 pr-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describe the project scope, objectives, and key activities..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all resize-none"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-100"></div>

            {/* Timeline */}
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Project Timeline</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Schedule & duration</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    Start Date <span className="text-orange-500">*</span>
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="w-full h-12 pl-11 pr-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    End Date
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className="w-full h-12 pl-11 pr-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-100"></div>

            {/* Location */}
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-violet-500" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Location</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Site information</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    Site Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g., Bokel Wind Farm Site"
                      className="w-full h-12 pl-11 pr-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    Country
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="e.g., Germany"
                      className="w-full h-12 pl-11 pr-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 text-sm font-medium transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-100"></div>

            {/* Status */}
            <div className="space-y-4">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                Initial Status
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                    formData.status === 'draft'
                      ? 'bg-slate-900 border-slate-900 text-white shadow-lg shadow-slate-900/20'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center ${
                    formData.status === 'draft' ? 'bg-white border-white' : 'border-slate-300'
                  }`}>
                    {formData.status === 'draft' && <CheckCircle2 className="h-3 w-3 text-slate-900" />}
                  </div>
                  Draft
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                    formData.status === 'active'
                      ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center ${
                    formData.status === 'active' ? 'bg-white border-white' : 'border-slate-300'
                  }`}>
                    {formData.status === 'active' && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                  </div>
                  Active
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-slate-100 bg-slate-50/50">
            <button
              type="button"
              onClick={() => navigate('/oem/projects')}
              className="h-11 px-6 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 font-bold text-[11px] uppercase tracking-widest transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="h-11 px-6 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-bold text-[11px] uppercase tracking-widest transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-orange-500/20"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OEMProjectCreate;
